import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Boxes, ChevronDown, ClipboardList, History, Layers3, PackageCheck, Plus, RefreshCw, Search, Truck, X } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import {
  formatInventoryMeasurement,
  formatInventoryMovementDeltaSummary,
  formatInventoryReceiptSummary,
} from '../lib/inventoryQuantityFormatting';
import { SHOW_DEVELOPMENT_PREVIEWS } from '../config/developmentPreviews';
import { ComingSoonBadge, DevelopmentPreviewSection } from '../ui/DevelopmentPreview';

type InventoryRow = {
  id: string;
  name: string;
  sku?: string | null;
  barcode?: string | null;
  category?: string | null;
  productBrand?: string | null;
  productType?: string | null;
  supplierId?: string | null;
  baseUnitId?: string | null;
  defaultPurchaseUnitId?: string | null;
  defaultConsumptionUnitId?: string | null;
  defaultDisplayUnitId?: string | null;
  baseUnit: string;
  unitOfMeasure?: string | null;
  consumableInVisit?: boolean;
  strength?: string | null;
  packageSize?: string | null;
  costPrice?: number;
  retailPrice?: number;
  trackLots?: boolean;
  trackExpiry?: boolean;
  reorderPoint: number;
  targetStock: number;
  reorderQuantity?: number;
  lotId?: string | null;
  brand?: string | null;
  supplier?: string | null;
  lotNumber?: string | null;
  expirationDate?: string | null;
  locationId?: string | null;
  locationName?: string | null;
  onHand: number;
};

type ProductRow = InventoryRow & {
  lots: InventoryRow[];
  totalOnHand: number;
};

type LocationOption = { id: string; name: string };
type UnitOption = { id: string; name: string; abbreviation: string; category: string; decimalPrecision?: number };
type ItemUnitRule = { id?: string; itemId?: string; unitId: string; name: string; abbreviation: string; category?: string; factorToBaseUnit: number; allowedForPurchase: boolean; allowedForConsumption: boolean; allowedForAdjustment: boolean; mustBeWholeNumber: boolean; displayOrder: number; active: boolean };
type SupplierRow = { id: string; name: string; contactName?: string | null; email?: string | null; phone?: string | null; address?: string | null; active?: boolean };
type PurchaseOrderRow = { id: string; supplierId?: string | null; status: string; expectedAt?: string | null; expectedDate?: string | null; notes?: string | null; createdAt?: string };
type PurchaseOrderLineRow = { id: string; purchaseOrderId: string; itemId: string; orderedQty: number; receivedQty: number; unitCost: number };
type GoodsReceiptRow = { id: string; purchaseOrderId?: string | null; itemId: string; lotId?: string | null; locationId: string; quantity: number; enteredQuantity?: number; enteredUnitLabel?: string | null; factorToBaseUnitAtTime?: number; baseQuantityReceived?: number; baseUnitLabel?: string | null; notes?: string | null; receivedAt?: string | null };
type MovementRow = { id: string; itemName: string; lotNumber?: string | null; locationName?: string | null; movementType?: string | null; type: string; quantity: number; enteredQuantity?: number; enteredUnitLabel?: string | null; quantityDelta?: number; baseQuantityDelta?: number; unit: string; baseUnitLabel?: string | null; reason?: string | null; occurredAt?: string };
type StockCountRow = { id: string; locationId: string; locationName?: string | null; status: string; startedAt?: string };
type StockCountLineRow = { id: string; stockCountId: string; itemName: string; lotNumber?: string | null; expectedQuantity: number; countedQuantity: number; variance: number };
type AlertsData = { lowStock: Array<{ name: string; onHand: number; reorderPoint: number }>; nearExpiry: Array<{ itemName: string; lotNumber: string; expirationDate: string }>; expired: Array<{ itemName: string; lotNumber: string; expirationDate: string }>; awaitingArrival: PurchaseOrderRow[]; recalledLots: Array<{ itemName: string; lotNumber: string }> };
type LowStockRow = { itemId: string; name: string; onHand: number; reorderPoint: number; targetStock: number; reorderQuantity?: number; suggestedOrderQuantity: number; unit: string };
type AdvancedData = { suppliers: SupplierRow[]; purchaseOrders: PurchaseOrderRow[]; purchaseOrderLines: PurchaseOrderLineRow[]; goodsReceipts: GoodsReceiptRow[] };
type StockCountData = { counts: StockCountRow[]; lines: StockCountLineRow[] };
type ProductOptionsData = { categories: string[]; brands: string[] };
type ViewKey = 'stock' | 'suppliers' | 'purchaseOrders' | 'movements' | 'stockCounts';
type ItemForm = { id?: string; name: string; sku: string; barcode: string; category: string; brand: string; productType: string; supplierId: string; baseUnit: string; unitOfMeasure: string; consumableInVisit: boolean; strength: string; packageSize: string; costPrice: number; retailPrice: number; trackLots: boolean; trackExpiry: boolean; reorderPoint: number; targetStock: number; reorderQuantity: number };
type LotForm = { itemId: string; brand: string; supplier: string; supplierId: string; lotNumber: string; expirationDate: string; receivedDate: string; unitCost: number; notes: string };
type MovementForm = { itemId: string; lotId: string; locationId: string; type: string; quantity: number; quantityDelta: number; unit: string; unitId: string; unitCost: number; reason: string; reference: string };
type SupplierForm = { name: string; contactName: string; email: string; phone: string; address: string; notes: string };
type PurchaseOrderFormLine = { clientId: string; itemId: string; orderedQty: number; unitCost: number };
type PurchaseOrderForm = { id?: string; supplierId: string; expectedAt: string; notes: string; lines: PurchaseOrderFormLine[] };
type PurchaseOrderReceiveFormLine = { lineId: string; itemId: string; itemName: string; openQuantity: number; quantity: number; unit: string; unitId: string; unitCost: number; lotId: string; lotNumber: string; expirationDate: string };
type PurchaseOrderReceiveForm = { purchaseOrderId: string; locationId: string; receivedDate: string; notes: string; lines: PurchaseOrderReceiveFormLine[] };
type GoodsReceiptForm = { purchaseOrderId: string; itemId: string; lotId: string; lotNumber: string; expirationDate: string; receivedDate: string; locationId: string; quantity: number; unit: string; unitId: string; unitCost: number; notes: string };
type StockCountForm = { locationId: string; notes: string };
type StockCountLineForm = { stockCountId: string; itemId: string; lotId: string; expectedQuantity: number; countedQuantity: number; unit: string; unitId: string };
type InventoryMode = 'operations' | 'setup';

const emptyItemForm: ItemForm = { name: '', sku: '', barcode: '', category: '', brand: '', productType: 'supply', supplierId: '', baseUnit: 'unit', unitOfMeasure: 'unit', consumableInVisit: true, strength: '', packageSize: '', costPrice: 0, retailPrice: 0, trackLots: false, trackExpiry: false, reorderPoint: 0, targetStock: 0, reorderQuantity: 0 };
const emptyLotForm: LotForm = { itemId: '', brand: '', supplier: '', supplierId: '', lotNumber: '', expirationDate: '', receivedDate: '', unitCost: 0, notes: '' };
const emptyMovementForm: MovementForm = { itemId: '', lotId: '', locationId: '', type: 'MANUAL_ADJUSTMENT', quantity: 1, quantityDelta: 0, unit: 'unit', unitId: '', unitCost: 0, reason: '', reference: '' };
const emptySupplierForm: SupplierForm = { name: '', contactName: '', email: '', phone: '', address: '', notes: '' };
const emptyGoodsReceiptForm: GoodsReceiptForm = { purchaseOrderId: '', itemId: '', lotId: '', lotNumber: '', expirationDate: '', receivedDate: '', locationId: '', quantity: 1, unit: 'unit', unitId: '', unitCost: 0, notes: '' };
const emptyStockCountForm: StockCountForm = { locationId: '', notes: '' };
const emptyStockCountLineForm: StockCountLineForm = { stockCountId: '', itemId: '', lotId: '', expectedQuantity: 0, countedQuantity: 0, unit: 'unit', unitId: '' };

function productFromRow(row: InventoryRow): ProductRow {
  return { ...row, lots: [], totalOnHand: 0 };
}

function itemFormFromProduct(product?: ProductRow): ItemForm {
  if (!product) return emptyItemForm;
  return {
    id: product.id,
    name: product.name || '',
    sku: product.sku || '',
    barcode: product.barcode || '',
    category: product.category || '',
    brand: product.productBrand || '',
    productType: product.productType || 'supply',
    supplierId: product.supplierId || '',
    baseUnit: product.baseUnit || 'unit',
    unitOfMeasure: product.unitOfMeasure || product.baseUnit || 'unit',
    consumableInVisit: product.consumableInVisit !== false,
    strength: product.strength || '',
    packageSize: product.packageSize || '',
    costPrice: Number(product.costPrice || 0),
    retailPrice: Number(product.retailPrice || 0),
    trackLots: Boolean(product.trackLots),
    trackExpiry: Boolean(product.trackExpiry),
    reorderPoint: Number(product.reorderPoint || 0),
    targetStock: Number(product.targetStock || 0),
    reorderQuantity: Number(product.reorderQuantity || 0),
  };
}

function extractApiErrorMessage(raw: string, fallback: string) {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.message === 'string') return parsed.message;
    if (typeof parsed === 'string') return parsed;
  } catch {}
  return raw?.trim() || fallback;
}

function toDateInputValue(date: Date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function formatOptionalInventoryGoal(value: number | null | undefined, unit: string) {
  return Number(value || 0) > 0 ? formatInventoryMeasurement(value || 0, unit) : 'Not set';
}

function formatPrintableNumber(value: number) {
  return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function createClientId() {
  return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function createPurchaseOrderFormLine(itemId = '', unitCost = 0): PurchaseOrderFormLine {
  return { clientId: createClientId(), itemId, orderedQty: 1, unitCost };
}

function createEmptyPurchaseOrderForm(supplierId = '', itemId = '', unitCost = 0): PurchaseOrderForm {
  return {
    id: undefined,
    supplierId,
    expectedAt: '',
    notes: '',
    lines: [createPurchaseOrderFormLine(itemId, unitCost)],
  };
}

function createEmptyPurchaseOrderReceiveForm(purchaseOrderId = '', locationId = ''): PurchaseOrderReceiveForm {
  return {
    purchaseOrderId,
    locationId,
    receivedDate: toDateInputValue(new Date()),
    notes: '',
    lines: [],
  };
}

export default function InventoryAutoReorderPage({ mode = 'operations' }: { mode?: InventoryMode }) {
  const { fetchJson, fetchApi } = useApiClient();
  const isSetup = mode === 'setup';
  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [catalogRows, setCatalogRows] = useState<InventoryRow[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [productOptions, setProductOptions] = useState<ProductOptionsData>({ categories: [], brands: [] });
  const [itemUnitsByProduct, setItemUnitsByProduct] = useState<Record<string, ItemUnitRule[]>>({});
  const [advanced, setAdvanced] = useState<AdvancedData>({ suppliers: [], purchaseOrders: [], purchaseOrderLines: [], goodsReceipts: [] });
  const [movements, setMovements] = useState<MovementRow[]>([]);
  const [alerts, setAlerts] = useState<AlertsData>({ lowStock: [], nearExpiry: [], expired: [], awaitingArrival: [], recalledLots: [] });
  const [lowStockRows, setLowStockRows] = useState<LowStockRow[]>([]);
  const [stockCounts, setStockCounts] = useState<StockCountData>({ counts: [], lines: [] });
  const [locationId, setLocationId] = useState('');
  const [search, setSearch] = useState('');
  const [stockStatusFilter, setStockStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [pricingScope, setPricingScope] = useState('Retail matrix');
  const [activeView, setActiveView] = useState<ViewKey>('stock');
  const [itemForm, setItemForm] = useState<ItemForm>(emptyItemForm);
  const [lotForm, setLotForm] = useState<LotForm>(emptyLotForm);
  const [movementForm, setMovementForm] = useState<MovementForm>(emptyMovementForm);
  const [supplierForm, setSupplierForm] = useState<SupplierForm>(emptySupplierForm);
  const [purchaseOrderForm, setPurchaseOrderForm] = useState<PurchaseOrderForm>(() => createEmptyPurchaseOrderForm());
  const [purchaseOrderReceiveForm, setPurchaseOrderReceiveForm] = useState<PurchaseOrderReceiveForm>(() => createEmptyPurchaseOrderReceiveForm());
  const [goodsReceiptForm, setGoodsReceiptForm] = useState<GoodsReceiptForm>(emptyGoodsReceiptForm);
  const [stockCountForm, setStockCountForm] = useState<StockCountForm>(emptyStockCountForm);
  const [stockCountLineForm, setStockCountLineForm] = useState<StockCountLineForm>(emptyStockCountLineForm);
  const [activeDialog, setActiveDialog] = useState<'item' | 'lot' | 'movement' | 'supplier' | 'purchaseOrder' | 'purchaseOrderReceive' | 'goodsReceipt' | 'stockCount' | 'stockCountLine' | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const visibleTabs = useMemo<Array<{ key: ViewKey; label: string }>>(() => (
    isSetup
      ? [
        { key: 'stock', label: 'Products' },
        { key: 'suppliers', label: 'Suppliers' },
      ]
      : [
        { key: 'stock', label: 'Current Stock' },
        { key: 'purchaseOrders', label: 'Purchase Orders' },
        { key: 'movements', label: 'Stock History' },
        { key: 'stockCounts', label: 'Counts' },
      ]
  ), [isSetup]);
  const roleSet = useMemo(() => new Set(userRoles.map((role) => role.toUpperCase())), [userRoles]);
  const canManageSetup = roleSet.has('ADMIN');
  const canManageOperations = roleSet.has('ADMIN') || roleSet.has('RECEPTIONIST');
  const canApproveCounts = roleSet.has('ADMIN');

  function purchaseOrderLinesForOrder(orderId: string) {
    return advanced.purchaseOrderLines.filter((line) => line.purchaseOrderId === orderId);
  }

  async function load() {
    setLoading(true);
    setError('');
    try {
      const queryParams = new URLSearchParams();
      if (locationId) queryParams.set('locationId', locationId);
      if (isSetup) queryParams.set('includeUnstocked', 'true');
      const params = queryParams.size ? `?${queryParams.toString()}` : '';
      const [summary, catalogSummary, locationList, unitData, optionData, advancedData, movementData, alertData, lowStockData, stockCountData] = await Promise.all([
        fetchJson<{ data: InventoryRow[] }>(`/inventory/summary${params}`),
        fetchJson<{ data: InventoryRow[] }>('/inventory/summary?includeUnstocked=true'),
        fetchJson<LocationOption[]>('/locations'),
        fetchJson<{ data: UnitOption[] }>('/inventory/units'),
        fetchJson<ProductOptionsData>('/inventory/product-options'),
        fetchJson<AdvancedData>('/inventory/advanced'),
        fetchJson<{ data: MovementRow[] }>('/inventory/movements'),
        fetchJson<AlertsData>('/inventory/alerts'),
        fetchJson<{ data: LowStockRow[] }>(`/inventory/low-stock${params}`),
        fetchJson<StockCountData>('/inventory/stock-counts'),
      ]);
      setRows(summary.data || []);
      setCatalogRows(catalogSummary.data || []);
      setLocations(locationList || []);
      setUnits(unitData.data || []);
      setProductOptions({ categories: optionData.categories || [], brands: optionData.brands || [] });
      setAdvanced({ suppliers: advancedData.suppliers || [], purchaseOrders: advancedData.purchaseOrders || [], purchaseOrderLines: advancedData.purchaseOrderLines || [], goodsReceipts: advancedData.goodsReceipts || [] });
      setMovements(movementData.data || []);
      setAlerts(alertData);
      setLowStockRows(lowStockData.data || []);
      setStockCounts({ counts: stockCountData.counts || [], lines: stockCountData.lines || [] });
      setMovementForm((current) => ({ ...current, locationId: current.locationId || locationList?.[0]?.id || '' }));
      setGoodsReceiptForm((current) => {
        const itemId = current.itemId || catalogSummary.data?.[0]?.id || '';
        const product = catalogSummary.data?.find((row) => row.id === itemId) || catalogSummary.data?.[0];
        return { ...current, locationId: current.locationId || locationList?.[0]?.id || '', itemId, unit: current.unit || product?.baseUnit || 'unit' };
      });
      setStockCountForm((current) => ({ ...current, locationId: current.locationId || locationList?.[0]?.id || '' }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load inventory.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [isSetup, locationId]);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const me = await fetchJson<{ role?: string; roles?: string[] }>('/users/me');
        const roles = (Array.isArray(me?.roles) ? me.roles : [me?.role]).map((role) => String(role || '').toUpperCase()).filter(Boolean);
        if (active) setUserRoles(roles);
      } catch {
        if (active) setUserRoles([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchJson]);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.key === activeView)) setActiveView('stock');
  }, [activeView, visibleTabs]);

  const products = useMemo(() => {
    const map = new Map<string, ProductRow>();
    rows.forEach((row) => {
      const product = map.get(row.id) || productFromRow(row);
      product.lots.push(row);
      product.totalOnHand += Number(row.onHand || 0);
      map.set(row.id, product);
    });
    return Array.from(map.values()).filter((product) => {
      const query = search.trim().toLowerCase();
      if (!query) return true;
      return [product.name, product.category, product.productBrand, product.strength, product.packageSize].some((value) => String(value || '').toLowerCase().includes(query));
    });
  }, [rows, search]);

  const catalogProducts = useMemo(() => {
    const map = new Map<string, ProductRow>();
    catalogRows.forEach((row) => {
      const product = map.get(row.id) || productFromRow(row);
      product.lots.push(row);
      product.totalOnHand += Number(row.onHand || 0);
      map.set(row.id, product);
    });
    return Array.from(map.values()).sort((left, right) => left.name.localeCompare(right.name));
  }, [catalogRows]);

  const lotOptions = useMemo(() => rows.filter((row) => row.lotId && (!movementForm.itemId || row.id === movementForm.itemId)), [movementForm.itemId, rows]);

  async function loadItemUnits(itemId: string, force = false) {
    if (!itemId || (!force && itemUnitsByProduct[itemId]?.length)) return;
    setUnitsLoading(true);
    try {
      const result = await fetchJson<{ data: ItemUnitRule[] }>(`/inventory/items/${itemId}/units`);
      setItemUnitsByProduct((current) => ({ ...current, [itemId]: result.data || [] }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load product units.');
    } finally {
      setUnitsLoading(false);
    }
  }

  function productUnitRules(itemId: string) {
    const product = catalogProducts.find((entry) => entry.id === itemId) || products.find((entry) => entry.id === itemId);
    const rules = itemUnitsByProduct[itemId]?.filter((rule) => rule.active) || [];
    if (rules.length) return rules;
    return product ? [{ unitId: product.baseUnitId || '', name: product.baseUnit, abbreviation: product.baseUnit, factorToBaseUnit: 1, allowedForPurchase: true, allowedForConsumption: true, allowedForAdjustment: true, mustBeWholeNumber: false, displayOrder: 0, active: true }] : [];
  }

  function unitOptionsForItem(itemId: string, purpose: 'purchase' | 'consumption' | 'adjustment') {
    const rules = productUnitRules(itemId).filter((rule) => (
      purpose === 'purchase' ? rule.allowedForPurchase : purpose === 'consumption' ? rule.allowedForConsumption : rule.allowedForAdjustment
    ));
    return rules.map((rule) => ({ value: rule.unitId || rule.abbreviation, label: rule.abbreviation || rule.name }));
  }

  function selectedUnitRule(itemId: string, unitId: string, unitLabel: string) {
    return productUnitRules(itemId).find((rule) => (unitId && rule.unitId === unitId) || rule.abbreviation === unitLabel || rule.name === unitLabel);
  }

  function receiptLotOptionsForItem(itemId: string) {
    const seenLotIds = new Set<string>();
    return catalogRows.filter((row) => {
      if (row.id !== itemId || !row.lotId || seenLotIds.has(row.lotId)) return false;
      seenLotIds.add(row.lotId);
      return true;
    });
  }

  function baseUnitPreview(itemId: string, quantity: number, unitId: string, unitLabel: string) {
    const product = catalogProducts.find((entry) => entry.id === itemId) || products.find((entry) => entry.id === itemId);
    const rule = selectedUnitRule(itemId, unitId, unitLabel);
    const baseQuantity = Math.abs(quantity || 0) * Number(rule?.factorToBaseUnit || 1);
    return `${baseQuantity} ${product?.baseUnit || rule?.abbreviation || unitLabel || 'unit'}`;
  }

  useEffect(() => {
    if (activeDialog === 'movement' && movementForm.itemId) void loadItemUnits(movementForm.itemId);
  }, [activeDialog, movementForm.itemId]);

  useEffect(() => {
    if (activeDialog === 'goodsReceipt' && goodsReceiptForm.itemId) void loadItemUnits(goodsReceiptForm.itemId);
  }, [activeDialog, goodsReceiptForm.itemId]);

  useEffect(() => {
    if (activeDialog === 'stockCountLine' && stockCountLineForm.itemId) void loadItemUnits(stockCountLineForm.itemId);
  }, [activeDialog, stockCountLineForm.itemId]);

  function openCreateItem() {
    setItemForm(emptyItemForm);
    setActiveDialog('item');
  }

  function openEditItem(product: ProductRow) {
    setItemForm(itemFormFromProduct(product));
    void loadItemUnits(product.id, true);
    setActiveDialog('item');
  }

  function openLotDialog(product?: ProductRow) {
    setLotForm({ ...emptyLotForm, itemId: product?.id || catalogProducts[0]?.id || '', receivedDate: toDateInputValue(new Date()) });
    setActiveDialog('lot');
  }

  function openMovementDialog(product?: ProductRow) {
    setMovementForm({
      ...emptyMovementForm,
      itemId: product?.id || products[0]?.id || '',
      unit: product?.baseUnit || products[0]?.baseUnit || 'unit',
      unitId: product?.baseUnitId || products[0]?.baseUnitId || '',
      locationId: locationId || locations[0]?.id || '',
    });
    if (product?.id || products[0]?.id) void loadItemUnits(product?.id || products[0]?.id || '');
    setActiveDialog('movement');
  }

  function openUseStockDialog(product?: ProductRow) {
    setMovementForm({
      ...emptyMovementForm,
      type: 'TREATMENT_USAGE',
      quantityDelta: -1,
      reason: 'Stock used',
      itemId: product?.id || products[0]?.id || '',
      unit: product?.baseUnit || products[0]?.baseUnit || 'unit',
      unitId: product?.baseUnitId || products[0]?.baseUnitId || '',
      locationId: locationId || locations[0]?.id || '',
    });
    if (product?.id || products[0]?.id) void loadItemUnits(product?.id || products[0]?.id || '');
    setActiveDialog('movement');
  }

  function openSupplierDialog() {
    setSupplierForm(emptySupplierForm);
    setActiveDialog('supplier');
  }

  function openPurchaseOrderDialog(order?: PurchaseOrderRow) {
    if (order) {
      const orderLines = purchaseOrderLinesForOrder(order.id);
      setPurchaseOrderForm({
        id: order.id,
        supplierId: order.supplierId || '',
        expectedAt: order.expectedAt || order.expectedDate ? toDateInputValue(new Date(order.expectedAt || order.expectedDate || '')) : '',
        notes: order.notes || '',
        lines: orderLines.length
          ? orderLines.map((line) => ({
            clientId: line.id,
            itemId: line.itemId,
            orderedQty: Number(line.orderedQty || 0),
            unitCost: Number(line.unitCost || 0),
          }))
          : [createPurchaseOrderFormLine(catalogProducts[0]?.id || '', Number(catalogProducts[0]?.costPrice || 0))],
      });
    } else {
      const defaultProduct = catalogProducts[0];
      setPurchaseOrderForm(createEmptyPurchaseOrderForm(advanced.suppliers[0]?.id || '', defaultProduct?.id || '', Number(defaultProduct?.costPrice || 0)));
    }
    setActiveDialog('purchaseOrder');
  }

  function openPurchaseOrderReceiveDialog(order: PurchaseOrderRow) {
    const openLines = advanced.purchaseOrderLines.filter((line) => line.purchaseOrderId === order.id && line.receivedQty < line.orderedQty);
    setPurchaseOrderReceiveForm({
      purchaseOrderId: order.id,
      locationId: locationId || locations[0]?.id || '',
      receivedDate: toDateInputValue(new Date()),
      notes: order.notes || '',
      lines: openLines.map((line) => {
        const product = catalogProducts.find((entry) => entry.id === line.itemId);
        const openQuantity = Math.max(0, Number(line.orderedQty || 0) - Number(line.receivedQty || 0));
        return {
          lineId: line.id,
          itemId: line.itemId,
          itemName: product?.name || line.itemId,
          openQuantity,
          quantity: openQuantity,
          unit: product?.baseUnit || 'unit',
          unitId: product?.baseUnitId || '',
          unitCost: Number(line.unitCost || product?.costPrice || 0),
          lotId: '',
          lotNumber: '',
          expirationDate: '',
        };
      }),
    });
    for (const itemId of new Set(openLines.map((line) => line.itemId))) {
      void loadItemUnits(itemId, true);
    }
    setActiveDialog('purchaseOrderReceive');
  }

  function openGoodsReceiptDialog(product?: ProductRow) {
    const preferredItemId = product?.id || catalogProducts[0]?.id || '';
    const preferredProduct = catalogProducts.find((entry) => entry.id === preferredItemId);
    setGoodsReceiptForm({
      ...emptyGoodsReceiptForm,
      itemId: preferredItemId,
      locationId: locationId || locations[0]?.id || '',
      quantity: 1,
      unit: preferredProduct?.baseUnit || 'unit',
      unitId: preferredProduct?.baseUnitId || '',
      unitCost: Number(preferredProduct?.costPrice || 0),
      receivedDate: toDateInputValue(new Date()),
      notes: '',
    });
    if (preferredItemId) void loadItemUnits(preferredItemId);
    setActiveDialog('goodsReceipt');
  }

  async function cancelPurchaseOrder(order: PurchaseOrderRow) {
    if (!window.confirm('Cancel this purchase order? It will stay in history but cannot be received again.')) return;
    await submit(`/inventory/purchase-orders/${order.id}/cancel`, 'POST', {});
  }

  function printPurchaseOrder(order: PurchaseOrderRow) {
    const orderLines = purchaseOrderLinesForOrder(order.id);
    const supplier = advanced.suppliers.find((entry) => entry.id === order.supplierId);
    const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=960,height=720');
    if (!printWindow) {
      setError('Allow pop-ups to print the purchase order.');
      return;
    }
    const total = orderLines.reduce((sum, line) => sum + Number(line.orderedQty || 0) * Number(line.unitCost || 0), 0);
    const lineRows = orderLines.map((line) => {
      const product = catalogProducts.find((entry) => entry.id === line.itemId);
      const lineTotal = Number(line.orderedQty || 0) * Number(line.unitCost || 0);
      return `
        <tr>
          <td>${escapeHtml(product?.name || line.itemId)}</td>
          <td>${escapeHtml(product?.sku || '-')}</td>
          <td>${escapeHtml(formatInventoryMeasurement(line.orderedQty, product?.baseUnit || 'unit'))}</td>
          <td>${escapeHtml(formatPrintableNumber(Number(line.unitCost || 0)))}</td>
          <td>${escapeHtml(formatPrintableNumber(lineTotal))}</td>
        </tr>`;
    }).join('');
    printWindow.document.write(`<!doctype html>
<html>
  <head>
    <title>Purchase Order ${escapeHtml(order.id)}</title>
    <style>
      body { font-family: "Segoe UI", Arial, sans-serif; margin: 32px; color: #0f172a; }
      h1 { margin: 0 0 4px; font-size: 24px; }
      .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 24px; margin: 24px 0; }
      .label { font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; }
      .value { margin-top: 4px; font-size: 14px; }
      table { width: 100%; border-collapse: collapse; margin-top: 24px; }
      th, td { border: 1px solid #cbd5e1; padding: 10px 12px; text-align: left; font-size: 14px; }
      th { background: #f8fafc; }
      .total { margin-top: 16px; text-align: right; font-size: 16px; font-weight: 600; }
      .notes { margin-top: 24px; padding: 12px; border: 1px solid #cbd5e1; background: #f8fafc; white-space: pre-wrap; }
    </style>
  </head>
  <body>
    <h1>Purchase Order</h1>
    <div class="label">Order ID</div>
    <div class="value">${escapeHtml(order.id)}</div>
    <div class="meta">
      <div>
        <div class="label">Supplier</div>
        <div class="value">${escapeHtml(supplier?.name || 'No supplier')}</div>
      </div>
      <div>
        <div class="label">Status</div>
        <div class="value">${escapeHtml(order.status || 'draft')}</div>
      </div>
      <div>
        <div class="label">Created</div>
        <div class="value">${escapeHtml(order.createdAt ? new Date(order.createdAt).toLocaleDateString() : new Date().toLocaleDateString())}</div>
      </div>
      <div>
        <div class="label">Expected</div>
        <div class="value">${escapeHtml(order.expectedAt || order.expectedDate ? new Date(order.expectedAt || order.expectedDate || '').toLocaleDateString() : '-')}</div>
      </div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Product</th>
          <th>SKU</th>
          <th>Ordered</th>
          <th>Unit Cost</th>
          <th>Line Total</th>
        </tr>
      </thead>
      <tbody>${lineRows}</tbody>
    </table>
    <div class="total">Estimated total: ${escapeHtml(formatPrintableNumber(total))}</div>
    ${order.notes ? `<div class="notes"><strong>Notes</strong><br />${escapeHtml(order.notes)}</div>` : ''}
    <script>window.onload = () => window.print();</script>
  </body>
</html>`);
    printWindow.document.close();
  }

  function openStockCountDialog() {
    setStockCountForm({ ...emptyStockCountForm, locationId: locationId || locations[0]?.id || '' });
    setActiveDialog('stockCount');
  }

  function openStockCountLineDialog(count: StockCountRow) {
    const product = catalogProducts[0];
    setStockCountLineForm({ ...emptyStockCountLineForm, stockCountId: count.id, itemId: product?.id || '', unit: product?.baseUnit || 'unit', unitId: product?.baseUnitId || '' });
    if (product?.id) void loadItemUnits(product.id);
    setActiveDialog('stockCountLine');
  }

  function addPurchaseOrderLine() {
    const defaultProduct = catalogProducts[0];
    setPurchaseOrderForm((form) => ({
      ...form,
      lines: [...form.lines, createPurchaseOrderFormLine(defaultProduct?.id || '', Number(defaultProduct?.costPrice || 0))],
    }));
  }

  function updatePurchaseOrderLine(clientId: string, patch: Partial<PurchaseOrderFormLine>) {
    setPurchaseOrderForm((form) => ({
      ...form,
      lines: form.lines.map((line) => (line.clientId === clientId ? { ...line, ...patch } : line)),
    }));
  }

  function removePurchaseOrderLine(clientId: string) {
    setPurchaseOrderForm((form) => ({
      ...form,
      lines: form.lines.length > 1 ? form.lines.filter((line) => line.clientId !== clientId) : form.lines,
    }));
  }

  function updatePurchaseOrderReceiveLine(lineId: string, patch: Partial<PurchaseOrderReceiveFormLine>) {
    setPurchaseOrderReceiveForm((form) => ({
      ...form,
      lines: form.lines.map((line) => (line.lineId === lineId ? { ...line, ...patch } : line)),
    }));
  }

  async function submit(path: string, method: 'POST' | 'PUT', body: unknown, options?: { closeDialog?: boolean }) {
    setSaving(true);
    setError('');
    try {
      const response = await fetchApi(path, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(extractApiErrorMessage(await response.text(), 'Unable to save inventory change.'));
      const raw = await response.text();
      const payload = raw ? JSON.parse(raw) : null;
      if (options?.closeDialog !== false) setActiveDialog(null);
      await load();
      return payload;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save inventory change.');
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function saveItem(event: React.FormEvent) {
    event.preventDefault();
    const body = {
      name: itemForm.name,
      sku: itemForm.sku || undefined,
      barcode: itemForm.barcode || undefined,
      category: itemForm.category || undefined,
      brand: itemForm.brand || undefined,
      productType: itemForm.productType,
      supplierId: itemForm.supplierId || undefined,
      baseUnit: itemForm.baseUnit,
      unitOfMeasure: itemForm.unitOfMeasure || itemForm.baseUnit,
      consumableInVisit: itemForm.consumableInVisit,
      strength: itemForm.strength || undefined,
      packageSize: itemForm.packageSize || undefined,
      costPrice: itemForm.costPrice,
      retailPrice: itemForm.retailPrice,
      trackLots: itemForm.trackLots,
      trackExpiry: itemForm.trackExpiry,
      reorderPoint: itemForm.reorderPoint,
      targetStock: itemForm.targetStock,
      reorderQuantity: itemForm.reorderQuantity,
    };
    if (itemForm.id) {
      await submit(`/inventory/items/${itemForm.id}`, 'PUT', body);
      return;
    }
    const payload = await submit('/inventory/items', 'POST', body, { closeDialog: false });
    if (payload?.id) {
      setItemForm((form) => ({ ...form, id: String(payload.id) }));
      await loadItemUnits(String(payload.id), true);
    }
  }

  function addItemUnitRule() {
    if (!itemForm.id) return;
    const current = itemUnitsByProduct[itemForm.id] || [];
    const used = new Set(current.map((rule) => rule.unitId));
    const nextUnit = units.find((unit) => !used.has(unit.id)) || units[0];
    if (!nextUnit) return;
    setItemUnitsByProduct((state) => ({
      ...state,
      [itemForm.id || '']: [
        ...current,
        {
          unitId: nextUnit.id,
          name: nextUnit.name,
          abbreviation: nextUnit.abbreviation,
          category: nextUnit.category,
          factorToBaseUnit: current.length ? 1 : 1,
          allowedForPurchase: true,
          allowedForConsumption: true,
          allowedForAdjustment: true,
          mustBeWholeNumber: false,
          displayOrder: current.length,
          active: true,
        },
      ],
    }));
  }

  function updateItemUnitRule(index: number, patch: Partial<ItemUnitRule>) {
    if (!itemForm.id) return;
    const current = itemUnitsByProduct[itemForm.id] || [];
    setItemUnitsByProduct((state) => ({
      ...state,
      [itemForm.id || '']: current.map((rule, rowIndex) => {
        if (rowIndex !== index) return rule;
        const selected = patch.unitId ? units.find((unit) => unit.id === patch.unitId) : null;
        return { ...rule, ...patch, name: selected?.name || patch.name || rule.name, abbreviation: selected?.abbreviation || patch.abbreviation || rule.abbreviation, category: selected?.category || patch.category || rule.category };
      }),
    }));
  }

  function removeItemUnitRule(index: number) {
    if (!itemForm.id || index === 0) return;
    const current = itemUnitsByProduct[itemForm.id] || [];
    setItemUnitsByProduct((state) => ({ ...state, [itemForm.id || '']: current.filter((_, rowIndex) => rowIndex !== index) }));
  }

  async function saveItemUnits() {
    if (!itemForm.id) return;
    const rules = (itemUnitsByProduct[itemForm.id] || []).filter((rule) => rule.unitId);
    if (!rules.length) {
      setError('Add at least the base unit before saving conversions.');
      return;
    }
    const normalizedRules = rules.map((rule, index) => ({
      ...rule,
      factorToBaseUnit: index === 0 ? 1 : Number(rule.factorToBaseUnit || 1),
      displayOrder: index,
      active: true,
    }));
    setSaving(true);
    setError('');
    try {
      const response = await fetchApi(`/inventory/items/${itemForm.id}/units`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          baseUnitId: normalizedRules[0].unitId,
          defaultPurchaseUnitId: normalizedRules.find((rule) => rule.allowedForPurchase)?.unitId || normalizedRules[0].unitId,
          defaultConsumptionUnitId: normalizedRules.find((rule) => rule.allowedForConsumption)?.unitId || normalizedRules[0].unitId,
          defaultDisplayUnitId: normalizedRules[0].unitId,
          units: normalizedRules.map((rule) => ({
            unitId: rule.unitId,
            factorToBaseUnit: rule.factorToBaseUnit,
            allowedForPurchase: rule.allowedForPurchase,
            allowedForConsumption: rule.allowedForConsumption,
            allowedForAdjustment: rule.allowedForAdjustment,
            mustBeWholeNumber: rule.mustBeWholeNumber,
            displayOrder: rule.displayOrder,
            active: true,
          })),
        }),
      });
      if (!response.ok) throw new Error(extractApiErrorMessage(await response.text(), 'Unable to save unit conversions.'));
      await loadItemUnits(itemForm.id, true);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save unit conversions.');
    } finally {
      setSaving(false);
    }
  }

  async function saveLot(event: React.FormEvent) {
    event.preventDefault();
    await submit('/inventory/lots', 'POST', { ...lotForm, supplierId: lotForm.supplierId || undefined, expirationDate: lotForm.expirationDate || undefined, receivedDate: lotForm.receivedDate || undefined });
  }

  async function saveMovement(event: React.FormEvent) {
    event.preventDefault();
    if (!movementForm.quantityDelta) {
      setError('Enter a stock change. Use a positive number to add stock or a negative number to remove stock.');
      return;
    }
    await submit('/inventory/movements', 'POST', {
      ...movementForm,
      lotId: movementForm.lotId || undefined,
      quantity: Math.abs(movementForm.quantityDelta),
      quantityDelta: movementForm.quantityDelta,
      unitId: movementForm.unitId || undefined,
      unitCost: movementForm.unitCost || undefined,
    });
  }

  async function saveSupplier(event: React.FormEvent) {
    event.preventDefault();
    await submit('/inventory/suppliers', 'POST', supplierForm);
  }

  async function savePurchaseOrder(event: React.FormEvent) {
    event.preventDefault();
    const lines = purchaseOrderForm.lines
      .filter((line) => line.itemId && Number(line.orderedQty || 0) > 0)
      .map((line) => ({ itemId: line.itemId, orderedQty: line.orderedQty, unitCost: line.unitCost }));
    if (!lines.length) {
      setError('Add at least one purchase-order line with a product and quantity.');
      return;
    }
    await submit(purchaseOrderForm.id ? `/inventory/purchase-orders/${purchaseOrderForm.id}` : '/inventory/purchase-orders', purchaseOrderForm.id ? 'PUT' : 'POST', {
      supplierId: purchaseOrderForm.supplierId || undefined,
      expectedAt: purchaseOrderForm.expectedAt || undefined,
      notes: purchaseOrderForm.notes || undefined,
      lines,
    });
  }

  async function savePurchaseOrderReceipt(event: React.FormEvent) {
    event.preventDefault();
    const selectedLines = purchaseOrderReceiveForm.lines.filter((line) => Number(line.quantity || 0) > 0);
    if (!selectedLines.length) {
      setError('Enter a received quantity for at least one line.');
      return;
    }
    const overReceivedLine = selectedLines.find((line) => Number(line.quantity || 0) > Number(line.openQuantity || 0));
    if (overReceivedLine) {
      setError(`Received quantity for ${overReceivedLine.itemName} cannot exceed the remaining open quantity.`);
      return;
    }
    const invalidTrackedLine = selectedLines.find((line) => {
      const product = catalogProducts.find((entry) => entry.id === line.itemId);
      if (!product?.trackLots) return false;
      if (!line.lotId && !line.lotNumber.trim()) return true;
      if (!line.lotId && product.trackExpiry && !line.expirationDate) return true;
      return false;
    });
    if (invalidTrackedLine) {
      const product = catalogProducts.find((entry) => entry.id === invalidTrackedLine.itemId);
      setError(product?.trackExpiry
        ? `Select an existing lot or enter a new lot and expiry date for ${invalidTrackedLine.itemName}.`
        : `Select an existing lot or enter a new lot for ${invalidTrackedLine.itemName}.`);
      return;
    }
    await submit(`/inventory/purchase-orders/${purchaseOrderReceiveForm.purchaseOrderId}/receive`, 'POST', {
      locationId: purchaseOrderReceiveForm.locationId,
      receivedDate: purchaseOrderReceiveForm.receivedDate || undefined,
      notes: purchaseOrderReceiveForm.notes || undefined,
      lines: selectedLines.map((line) => ({
        lineId: line.lineId,
        itemId: line.itemId,
        quantity: line.quantity,
        unit: line.unit,
        unitId: line.unitId || undefined,
        lotId: line.lotId || undefined,
        lotNumber: line.lotNumber || undefined,
        expirationDate: line.expirationDate || undefined,
        unitCost: line.unitCost || undefined,
      })),
    });
  }

  async function saveGoodsReceipt(event: React.FormEvent) {
    event.preventDefault();
    await submit('/inventory/goods-receipts', 'POST', {
      purchaseOrderId: goodsReceiptForm.purchaseOrderId || undefined,
      itemId: goodsReceiptForm.itemId,
      lotId: goodsReceiptForm.lotId || undefined,
      lotNumber: goodsReceiptForm.lotNumber || undefined,
      expirationDate: goodsReceiptForm.expirationDate || undefined,
      receivedDate: goodsReceiptForm.receivedDate || undefined,
      locationId: goodsReceiptForm.locationId,
      quantity: goodsReceiptForm.quantity,
      unit: goodsReceiptForm.unit,
      unitId: goodsReceiptForm.unitId || undefined,
      unitCost: goodsReceiptForm.unitCost || undefined,
      notes: goodsReceiptForm.notes || undefined,
    });
  }

  async function saveStockCount(event: React.FormEvent) {
    event.preventDefault();
    const payload = await submit('/inventory/stock-counts', 'POST', stockCountForm, { closeDialog: false });
    if (!payload?.id) return;
    const createdCount: StockCountRow = {
      id: String(payload.id),
      locationId: stockCountForm.locationId,
      locationName: locations.find((location) => location.id === stockCountForm.locationId)?.name || null,
      status: 'draft',
      startedAt: new Date().toISOString(),
    };
    setActiveView('stockCounts');
    openStockCountLineDialog(createdCount);
  }

  async function saveStockCountLine(event: React.FormEvent) {
    event.preventDefault();
    await submit(`/inventory/stock-counts/${stockCountLineForm.stockCountId}/lines`, 'POST', {
      itemId: stockCountLineForm.itemId,
      lotId: stockCountLineForm.lotId || undefined,
      expectedQuantity: stockCountLineForm.expectedQuantity,
      countedQuantity: stockCountLineForm.countedQuantity,
      unit: stockCountLineForm.unit,
      unitId: stockCountLineForm.unitId || undefined,
    });
  }

  async function approveStockCount(count: StockCountRow) {
    await submit(`/inventory/stock-counts/${count.id}/approve`, 'POST', {});
  }

  const itemDialogUnitRules = itemForm.id ? itemUnitsByProduct[itemForm.id] || [] : [];
  const selectedMovementProduct = products.find((item) => item.id === movementForm.itemId);
  const selectedGoodsReceiptProduct = catalogProducts.find((item) => item.id === goodsReceiptForm.itemId);
  const selectedStockCountProduct = catalogProducts.find((item) => item.id === stockCountLineForm.itemId);
  const movementPurpose = movementForm.type === 'PURCHASE_RECEIVED' || movementForm.type === 'CLIENT_RETURN' ? 'purchase' : movementForm.type === 'TREATMENT_USAGE' ? 'consumption' : 'adjustment';
  const movementUnitOptions = unitOptionsForItem(movementForm.itemId, movementPurpose);
  const movementTracksLots = Boolean(selectedMovementProduct?.trackLots);
  const receiptUnitOptions = unitOptionsForItem(goodsReceiptForm.itemId, 'purchase');
  const receiptLotOptions = useMemo(() => receiptLotOptionsForItem(goodsReceiptForm.itemId), [catalogRows, goodsReceiptForm.itemId]);
  const receiptTracksLots = Boolean(selectedGoodsReceiptProduct?.trackLots);
  const receiptTracksExpiry = Boolean(selectedGoodsReceiptProduct?.trackExpiry);
  const isCreatingNewReceiptLot = !goodsReceiptForm.lotId;
  const stockCountUnitOptions = unitOptionsForItem(stockCountLineForm.itemId, 'adjustment');
  const stockCountTracksLots = Boolean(selectedStockCountProduct?.trackLots);

  return (
    <div className="space-y-5 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm uppercase tracking-wide text-slate-400">{isSetup ? 'Settings' : 'Operations'}</p>
          <h1 className="text-2xl font-semibold text-ink">{isSetup ? 'Inventory Setup' : 'Inventory'}</h1>
          <p className="text-sm text-slate-500">
            {isSetup
              ? 'Set up products, suppliers, lot rules, and reorder thresholds.'
              : 'Receive stock, use stock, count inventory, and review what needs attention today.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isSetup && canManageSetup ? (
            <>
              <button onClick={openCreateItem} className="inline-flex items-center gap-2 rounded-lg bg-ink px-3 py-2 text-sm font-semibold text-white">
                <Plus size={15} /> Add product
              </button>
              <button onClick={() => openLotDialog()} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                <PackageCheck size={15} /> Add lot/batch
              </button>
              <button onClick={openSupplierDialog} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                <Truck size={15} /> Add supplier
              </button>
            </>
          ) : null}
          {!isSetup && canManageOperations ? (
            <>
              <button onClick={() => openGoodsReceiptDialog()} className="inline-flex items-center gap-2 rounded-lg bg-ink px-3 py-2 text-sm font-semibold text-white">
                <Truck size={15} /> Receive stock
              </button>
              <button onClick={() => openUseStockDialog()} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                <PackageCheck size={15} /> Use stock
              </button>
              <button onClick={() => openMovementDialog()} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                <RefreshCw size={15} /> Adjust stock
              </button>
              <button onClick={openStockCountDialog} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                <ClipboardList size={15} /> Start count
              </button>
              <button onClick={() => openPurchaseOrderDialog()} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                <PackageCheck size={15} /> New order
              </button>
            </>
          ) : null}
        </div>
      </header>

      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {isSetup && !canManageSetup ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">Inventory setup is read-only for your role. An admin can add products, suppliers, lots, and reorder rules.</div>
      ) : null}
      {!isSetup && !canManageOperations ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">You can review inventory here. Stock receiving, usage, adjustments, and counts are handled by admin or reception roles.</div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-5">
        <AlertCard label="Low stock" value={alerts.lowStock.length} tone="amber" />
        <AlertCard label="Near expiry" value={alerts.nearExpiry.length} tone="sky" />
        <AlertCard label="Expired" value={alerts.expired.length} tone="rose" />
        <AlertCard label="Awaiting arrival" value={alerts.awaitingArrival.length} tone="emerald" />
        <AlertCard label="Recalled lots" value={alerts.recalledLots.length} tone="slate" />
      </div>

      {SHOW_DEVELOPMENT_PREVIEWS ? (
        <DevelopmentPreviewSection
          title="Development preview: inventory roadmap sections"
          detail="These cards are not wired to working workflows yet. They stay visible in development as a reminder, and they are hidden in production."
        >
          <section className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold text-ink">Product operations</h2>
                    <ComingSoonBadge />
                  </div>
                  <p className="text-sm text-slate-500">Scanner setup, inactive filters, product types, tax, consumption, and bulk maintenance.</p>
                </div>
                <select value={stockStatusFilter} onChange={(event) => setStockStatusFilter(event.target.value as 'active' | 'inactive' | 'all')} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
                  <option value="active">Active products</option>
                  <option value="inactive">Inactive products</option>
                  <option value="all">All products</option>
                </select>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                <InventoryFeatureCard icon={<Search size={16} />} title="Scanner setup" detail="Barcode scanner pairing, POS scan action, and quick receive-by-scan controls." />
                <InventoryFeatureCard icon={<Boxes size={16} />} title="Product types" detail="Retail, consumable, injectable, and supply setup with injectable strength and package controls." />
                <InventoryFeatureCard icon={<PackageCheck size={16} />} title="Automatic consumption" detail="Link products to services so visit completion decrements consumables and injectables." />
                <InventoryFeatureCard icon={<ClipboardList size={16} />} title="Bulk editor" detail="Bulk update active state, supplier, tax, category, reorder levels, and online/POS visibility." />
                <InventoryFeatureCard icon={<Layers3 size={16} />} title="Tax assignment" detail="Set tax/VAT by product, product type, location, or supplier category." />
                <InventoryFeatureCard icon={<History size={16} />} title="Movement report" detail="Export stock movement history by product, lot, location, type, and date range." />
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold text-ink">Pricing and count controls</h2>
                    <ComingSoonBadge />
                  </div>
                  <p className="text-sm text-slate-500">Pricing matrix, purchase order receiving, and stock count lifecycle controls.</p>
                </div>
                <select value={pricingScope} onChange={(event) => setPricingScope(event.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
                  {['Retail matrix', 'Location prices', 'Staff discount rules', 'Supplier cost matrix'].map((option) => <option key={option}>{option}</option>)}
                </select>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <InventoryFeatureCard icon={<Truck size={16} />} title="PO partial receive" detail="Build purchase orders, receive partial quantities, complete later, or receive without an order." />
                <InventoryFeatureCard icon={<RefreshCw size={16} />} title="Count lifecycle" detail="Start, pause, resume, finish, approve, or delete inventory counts with variance review." />
                <InventoryFeatureCard icon={<PackageCheck size={16} />} title="Pricing matrix" detail={`${pricingScope} supports location, package, tax, wholesale, and retail price columns.`} />
                <InventoryFeatureCard icon={<AlertTriangle size={16} />} title="Audit-safe changes" detail="Deactivations, count corrections, and stock movements are recorded as history rows." />
              </div>
            </div>
          </section>
        </DevelopmentPreviewSection>
      ) : null}

      <div className="flex flex-wrap gap-2 border-b border-slate-200">
        {visibleTabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveView(key)}
            className={`border-b-2 px-3 py-2 text-sm font-medium ${activeView === key ? 'border-ink text-ink' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeView === 'stock' ? (
      <>
      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-ink">{isSetup ? 'Products' : 'Current stock'}</h2>
            <p className="text-xs text-slate-500">
              {isSetup ? 'Click a product row to edit its setup.' : 'Search and filter stock without changing product setup.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <input className="min-w-[240px] rounded-md border border-slate-300 py-2 pl-8 pr-3 text-sm" placeholder="Search products..." value={search} onChange={(event) => setSearch(event.target.value)} />
            </div>
            <select className="rounded-md border border-slate-300 px-2 py-2 text-sm" value={locationId} onChange={(event) => setLocationId(event.target.value)}>
              <option value="">All locations</option>
              {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
            </select>
          </div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Loading inventory...</div>
        ) : products.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No products yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="p-4">Product</th>
                  <th className="p-4">Category</th>
                  <th className="p-4">Unit</th>
                  <th className="p-4">On Hand</th>
                  <th className="p-4">Reorder / Goal</th>
                  <th className="p-4">Lots</th>
                  <th className="p-4">Next Expiry</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {products.map((product) => {
                  const below = product.totalOnHand < product.reorderPoint;
                  const nextExpiry = product.lots.map((lot) => lot.expirationDate).filter(Boolean).sort()[0];
                  return (
                    <tr key={product.id} onClick={isSetup && canManageSetup ? () => openEditItem(product) : undefined} className={`${isSetup && canManageSetup ? 'cursor-pointer' : ''} hover:bg-slate-50`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-50 text-sky-700"><Boxes size={16} /></span>
                          <div>
                            <p className="font-semibold text-ink">{product.name}</p>
                            <p className="text-xs text-slate-500">{[product.productBrand, product.strength, product.packageSize].filter(Boolean).join(' · ') || 'No package details'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-slate-600">{product.category || 'General'}</td>
                      <td className="p-4 text-slate-600">{product.baseUnit}</td>
                      <td className={`p-4 font-medium ${below ? 'text-rose-600' : 'text-slate-700'}`}>{formatInventoryMeasurement(product.totalOnHand, product.baseUnit)}</td>
                      <td className="p-4 text-slate-600">
                        <div className="font-medium text-slate-700">{formatInventoryMeasurement(product.reorderPoint, product.baseUnit)}</div>
                        <div className="text-xs text-slate-500">Goal: {formatOptionalInventoryGoal(product.targetStock, product.baseUnit)}</div>
                      </td>
                      <td className="p-4 text-slate-600">{product.lots.filter((lot) => lot.lotId).length}</td>
                      <td className="p-4 text-slate-600">{nextExpiry ? new Date(nextExpiry).toLocaleDateString() : 'None'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {isSetup ? (
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700"><Layers3 size={16} /></span>
            <div>
              <h2 className="text-sm font-semibold text-ink">Units and conversions</h2>
              <p className="mt-1 text-sm text-slate-500">Open a product to add pack, box, vial, ml, tablet, or other allowed units. The first row is the base unit; extra rows convert back to that base unit during receiving, usage, adjustments, and visit completion.</p>
            </div>
          </div>
        </section>
      ) : (
      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-ink">Low stock</h2>
          <p className="text-xs text-slate-500">{lowStockRows.length} low-stock rows below reorder thresholds. Suggested orders use the restock goal first, then reorder quantity when no goal is set.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="p-4">Product</th><th className="p-4">On Hand</th><th className="p-4">Reorder</th><th className="p-4">Restock Goal</th><th className="p-4">Suggested Order</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lowStockRows.map((row) => <tr key={row.itemId}><td className="p-4 font-medium text-ink">{row.name}</td><td className="p-4 text-slate-700">{formatInventoryMeasurement(row.onHand, row.unit)}</td><td className="p-4 text-slate-700">{formatInventoryMeasurement(row.reorderPoint, row.unit)}</td><td className="p-4 text-slate-700">{formatOptionalInventoryGoal(row.targetStock, row.unit)}</td><td className="p-4 text-slate-700">{formatInventoryMeasurement(row.suggestedOrderQuantity, row.unit)}</td></tr>)}
            </tbody>
          </table>
        </div>
      </section>
      )}
      </>
      ) : null}

      {activeView === 'suppliers' ? (
        <SimpleTable title="Suppliers" description="Active supplier contacts for product purchasing.">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr><th className="p-4">Name</th><th className="p-4">Contact</th><th className="p-4">Email</th><th className="p-4">Phone</th><th className="p-4">Address</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {advanced.suppliers.map((supplier) => <tr key={supplier.id}><td className="p-4 font-medium text-ink">{supplier.name}</td><td className="p-4 text-slate-600">{supplier.contactName || '-'}</td><td className="p-4 text-slate-600">{supplier.email || '-'}</td><td className="p-4 text-slate-600">{supplier.phone || '-'}</td><td className="p-4 text-slate-600">{supplier.address || '-'}</td></tr>)}
          </tbody>
        </SimpleTable>
      ) : null}

      {activeView === 'purchaseOrders' ? (
        <>
          <SimpleTable title="Purchase Orders" description="Orders awaiting arrival, partial receipt, or completion.">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="p-4">Supplier</th><th className="p-4">Status</th><th className="p-4">Expected</th><th className="p-4">Line Items</th><th className="p-4">Notes</th><th className="p-4 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {advanced.purchaseOrders.map((order) => {
                const lines = purchaseOrderLinesForOrder(order.id);
                const hasOpenQuantity = lines.some((line) => line.receivedQty < line.orderedQty);
                const hasReceivedQuantity = lines.some((line) => Number(line.receivedQty || 0) > 0);
                const normalizedStatus = String(order.status || '').toLowerCase();
                const canEditOrder = !hasReceivedQuantity && !['cancelled', 'received'].includes(normalizedStatus);
                const canCancelOrder = !['cancelled', 'received'].includes(normalizedStatus);
                const canReceiveOrder = hasOpenQuantity && normalizedStatus !== 'cancelled';
                return <tr key={order.id}><td className="p-4 text-slate-700">{advanced.suppliers.find((supplier) => supplier.id === order.supplierId)?.name || 'No supplier'}</td><td className="p-4 text-slate-700">{order.status}</td><td className="p-4 text-slate-600">{order.expectedAt || order.expectedDate ? new Date(order.expectedAt || order.expectedDate || '').toLocaleDateString() : '-'}</td><td className="p-4 text-slate-600"><div className="space-y-2">{lines.length ? lines.map((line) => {
                  const product = catalogProducts.find((entry) => entry.id === line.itemId);
                  const remainingQty = Math.max(0, Number(line.orderedQty || 0) - Number(line.receivedQty || 0));
                  return <div key={line.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><div className="font-medium text-slate-700">{product?.name || line.itemId}</div><div className="text-xs text-slate-500">Ordered {formatInventoryMeasurement(line.orderedQty, product?.baseUnit || 'unit')} · Received {formatInventoryMeasurement(line.receivedQty, product?.baseUnit || 'unit')} · Remaining {formatInventoryMeasurement(remainingQty, product?.baseUnit || 'unit')}</div></div>;
                }) : <span>-</span>}</div></td><td className="p-4 text-slate-600">{order.notes || '-'}</td><td className="p-4 text-right">{canManageOperations ? <div className="flex flex-wrap justify-end gap-2"><button type="button" onClick={() => printPurchaseOrder(order)} className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700">Print</button><button type="button" onClick={() => openPurchaseOrderDialog(order)} disabled={!canEditOrder} className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 disabled:cursor-not-allowed disabled:opacity-50">Edit</button><button type="button" onClick={() => void cancelPurchaseOrder(order)} disabled={!canCancelOrder} className="rounded-md border border-rose-200 px-2 py-1 text-xs text-rose-700 disabled:cursor-not-allowed disabled:opacity-50">Cancel</button><button type="button" onClick={() => openPurchaseOrderReceiveDialog(order)} disabled={!canReceiveOrder} className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 disabled:cursor-not-allowed disabled:opacity-50">Receive order</button></div> : <span className="text-xs text-slate-400">Read only</span>}</td></tr>;
              })}
            </tbody>
          </SimpleTable>
          <SimpleTable title="Recent Goods Receipts" description="Recently received inventory recorded into stock.">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="p-4">Date</th><th className="p-4">Product</th><th className="p-4">Location</th><th className="p-4">Quantity</th><th className="p-4">Purchase Order</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(advanced.goodsReceipts || []).map((receipt) => {
                const product = products.find((row) => row.id === receipt.itemId);
                const location = locations.find((row) => row.id === receipt.locationId);
                const receiptSummary = formatInventoryReceiptSummary(receipt, product?.baseUnit);
                return <tr key={receipt.id}><td className="p-4 text-slate-600">{receipt.receivedAt ? new Date(receipt.receivedAt).toLocaleString() : '-'}</td><td className="p-4 font-medium text-ink">{product?.name || receipt.itemId}</td><td className="p-4 text-slate-600">{location?.name || receipt.locationId}</td><td className="p-4 text-slate-700"><div className="font-medium text-slate-800">{receiptSummary.primary}</div>{receiptSummary.secondary ? <div className="text-xs text-slate-500">{receiptSummary.secondary}</div> : null}</td><td className="p-4 text-slate-600">{receipt.purchaseOrderId || '-'}</td></tr>;
              })}
            </tbody>
          </SimpleTable>
        </>
      ) : null}

      {activeView === 'movements' ? (
        <SimpleTable title="Stock History" description="Every stock change is listed here. Corrections are added as a new adjustment.">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr><th className="p-4">Date</th><th className="p-4">Product</th><th className="p-4">Lot</th><th className="p-4">Location</th><th className="p-4">Type</th><th className="p-4">Delta</th><th className="p-4">Reason</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {movements.map((movement) => {
              const movementSummary = formatInventoryMovementDeltaSummary(movement);
              return <tr key={movement.id}><td className="p-4 text-slate-600">{movement.occurredAt ? new Date(movement.occurredAt).toLocaleString() : '-'}</td><td className="p-4 font-medium text-ink">{movement.itemName}</td><td className="p-4 text-slate-600">{movement.lotNumber || '-'}</td><td className="p-4 text-slate-600">{movement.locationName || '-'}</td><td className="p-4 text-slate-700">{movement.movementType || movement.type}</td><td className="p-4 font-medium text-slate-800"><div>{movementSummary.primary}</div><div className="text-xs font-normal text-slate-500">{movementSummary.secondary}</div></td><td className="p-4 text-slate-600">{movement.reason || '-'}</td></tr>;
            })}
          </tbody>
        </SimpleTable>
      ) : null}

      {activeView === 'stockCounts' ? (
        <SimpleTable title="Stock Counts" description="Count a location, record variances, then approve corrections.">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr><th className="p-4">Location</th><th className="p-4">Status</th><th className="p-4">Started</th><th className="p-4">Lines</th><th className="p-4 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {stockCounts.counts.map((count) => <tr key={count.id}><td className="p-4 text-slate-700">{count.locationName || count.locationId}</td><td className="p-4 text-slate-700">{count.status}</td><td className="p-4 text-slate-600">{count.startedAt ? new Date(count.startedAt).toLocaleString() : '-'}</td><td className="p-4 text-slate-600">{stockCounts.lines.filter((line) => line.stockCountId === count.id).length}</td><td className="p-4 text-right">{canManageOperations ? <button type="button" onClick={() => openStockCountLineDialog(count)} className="mr-2 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700">Add line</button> : null}{canApproveCounts ? <button type="button" onClick={() => void approveStockCount(count)} className="rounded-md bg-ink px-2 py-1 text-xs font-semibold text-white">Approve</button> : <span className="text-xs text-slate-400">Read only</span>}</td></tr>)}
          </tbody>
        </SimpleTable>
      ) : null}

      {activeDialog === 'item' ? (
        <Dialog title={itemForm.id ? 'Edit Product' : 'Add Product'} onClose={() => setActiveDialog(null)}>
          <form className="space-y-4" onSubmit={saveItem}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <TextInput label="Name" value={itemForm.name} onChange={(value) => setItemForm((form) => ({ ...form, name: value }))} required />
              <TextInput label="SKU" value={itemForm.sku} onChange={(value) => setItemForm((form) => ({ ...form, sku: value }))} />
              <TextInput label="Barcode" value={itemForm.barcode} onChange={(value) => setItemForm((form) => ({ ...form, barcode: value }))} />
              <SuggestionInput label="Category" value={itemForm.category} onChange={(value) => setItemForm((form) => ({ ...form, category: value }))} options={productOptions.categories} placeholder="Search or add a category" emptyMessage="Start typing to filter saved categories." />
              <SuggestionInput label="Brand" value={itemForm.brand} onChange={(value) => setItemForm((form) => ({ ...form, brand: value }))} options={productOptions.brands} placeholder="Search or add a brand" emptyMessage="Start typing to filter saved brands." />
              <SelectInput label="Product Type" value={itemForm.productType} onChange={(value) => setItemForm((form) => ({ ...form, productType: value }))} options={[
                { value: 'retail', label: 'Retail' },
                { value: 'consumable', label: 'Consumable' },
                { value: 'injectable', label: 'Injectable' },
                { value: 'supply', label: 'Supply' },
              ]} />
              <SelectInput label="Supplier" value={itemForm.supplierId} onChange={(value) => setItemForm((form) => ({ ...form, supplierId: value }))} options={[{ value: '', label: 'No supplier' }, ...advanced.suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name }))]} />
              <TextInput label="Base Unit" value={itemForm.baseUnit} onChange={(value) => setItemForm((form) => ({ ...form, baseUnit: value }))} required />
              <TextInput label="Unit of Measure" value={itemForm.unitOfMeasure} onChange={(value) => setItemForm((form) => ({ ...form, unitOfMeasure: value }))} />
              <TextInput label="Strength" value={itemForm.strength} onChange={(value) => setItemForm((form) => ({ ...form, strength: value }))} />
              <TextInput label="Package Size" value={itemForm.packageSize} onChange={(value) => setItemForm((form) => ({ ...form, packageSize: value }))} />
              <NumberInput label="Cost Price" value={itemForm.costPrice} onChange={(value) => setItemForm((form) => ({ ...form, costPrice: value }))} step={0.01} />
              <NumberInput label="Retail Price" value={itemForm.retailPrice} onChange={(value) => setItemForm((form) => ({ ...form, retailPrice: value }))} step={0.01} />
              <NumberInput label={`Reorder Point (${itemForm.baseUnit || 'unit'})`} value={itemForm.reorderPoint} onChange={(value) => setItemForm((form) => ({ ...form, reorderPoint: value }))} />
              <NumberInput label={`Restock Goal (${itemForm.baseUnit || 'unit'})`} value={itemForm.targetStock} onChange={(value) => setItemForm((form) => ({ ...form, targetStock: value }))} />
              <NumberInput label={`Default Reorder Qty (${itemForm.baseUnit || 'unit'})`} value={itemForm.reorderQuantity} onChange={(value) => setItemForm((form) => ({ ...form, reorderQuantity: value }))} />
            </div>
            <p className="-mt-1 text-xs text-slate-500">Reorder point triggers alerts. Restock goal is optional and sets the preferred level to refill back to. Default reorder quantity is used for suggestions when no restock goal is set.</p>
            <div className="flex flex-wrap gap-4">
              <CheckboxInput label="Track lots/batches" checked={itemForm.trackLots} onChange={(value) => setItemForm((form) => ({ ...form, trackLots: value }))} />
              <CheckboxInput label="Track expiry" checked={itemForm.trackExpiry} onChange={(value) => setItemForm((form) => ({ ...form, trackExpiry: value }))} />
              <CheckboxInput label="Can be used during visits" checked={itemForm.consumableInVisit} onChange={(value) => setItemForm((form) => ({ ...form, consumableInVisit: value }))} />
            </div>
            <section className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-ink">Units and Conversions</h3>
                  <p className="text-xs text-slate-500">Keep the first row as the smallest unit you count on hand. Extra rows are only needed when staff receive or use packs, boxes, vials, or similar units.</p>
                </div>
                <button type="button" onClick={addItemUnitRule} disabled={!itemForm.id || !units.length || unitsLoading} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 disabled:opacity-50">
                  <Plus size={14} /> Add unit
                </button>
              </div>
              {!itemForm.id ? (
                <div className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-3 text-xs text-slate-600">Save this product once and this section unlocks here immediately so you can add the conversion rows without leaving the screen.</div>
              ) : unitsLoading && !itemDialogUnitRules.length ? <p className="text-sm text-slate-500">Loading units...</p> : itemDialogUnitRules.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="text-slate-500">
                      <tr><th className="p-2">Unit</th><th className="p-2">Equals Base</th><th className="p-2">Receive</th><th className="p-2">Use</th><th className="p-2">Adjust</th><th className="p-2">Whole</th><th className="p-2"></th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {itemDialogUnitRules.map((rule, index) => (
                        <tr key={`${rule.unitId}-${index}`}>
                          <td className="min-w-[150px] p-2">
                            <select className="w-full rounded-md border border-slate-300 px-2 py-1.5" value={rule.unitId} onChange={(event) => updateItemUnitRule(index, { unitId: event.target.value })} disabled={index === 0}>
                              {units.map((unit) => <option key={unit.id} value={unit.id}>{unit.abbreviation} - {unit.name}</option>)}
                            </select>
                            {index === 0 ? <span className="mt-1 inline-block text-[11px] font-medium text-emerald-700">Base unit</span> : null}
                          </td>
                          <td className="min-w-[150px] p-2">
                            <div className="flex items-center gap-2">
                              <span>1</span>
                              <span className="text-slate-500">=</span>
                              <input type="number" min="0.000001" step="0.000001" disabled={index === 0} className="w-24 rounded-md border border-slate-300 px-2 py-1.5 disabled:bg-slate-100" value={index === 0 ? 1 : rule.factorToBaseUnit} onChange={(event) => updateItemUnitRule(index, { factorToBaseUnit: Number(event.target.value) || 1 })} />
                              <span className="text-slate-500">{itemDialogUnitRules[0]?.abbreviation || itemForm.baseUnit}</span>
                            </div>
                          </td>
                          <td className="p-2"><input type="checkbox" checked={rule.allowedForPurchase} onChange={(event) => updateItemUnitRule(index, { allowedForPurchase: event.target.checked })} /></td>
                          <td className="p-2"><input type="checkbox" checked={rule.allowedForConsumption} onChange={(event) => updateItemUnitRule(index, { allowedForConsumption: event.target.checked })} /></td>
                          <td className="p-2"><input type="checkbox" checked={rule.allowedForAdjustment} onChange={(event) => updateItemUnitRule(index, { allowedForAdjustment: event.target.checked })} /></td>
                          <td className="p-2"><input type="checkbox" checked={rule.mustBeWholeNumber} onChange={(event) => updateItemUnitRule(index, { mustBeWholeNumber: event.target.checked })} /></td>
                          <td className="p-2 text-right">
                            {index === 0 ? null : <button type="button" onClick={() => removeItemUnitRule(index)} className="rounded-md p-1 text-slate-500 hover:bg-white hover:text-rose-600" aria-label="Remove unit"><X size={14} /></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-sm text-slate-500">The base unit is ready. Add extra units only if this item is received or used in packs, boxes, vials, ml, or similar units.</p>}
              <div className="mt-3 flex justify-end">
                <button type="button" onClick={() => void saveItemUnits()} disabled={saving || !itemForm.id || !itemDialogUnitRules.length} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50">Save units</button>
              </div>
            </section>
            <DialogActions saving={saving} saveLabel={itemForm.id ? 'Save product' : 'Save and continue'} onCancel={() => setActiveDialog(null)} />
          </form>
        </Dialog>
      ) : null}

      {activeDialog === 'lot' ? (
        <Dialog title="Add Lot or Batch" onClose={() => setActiveDialog(null)}>
          <form className="space-y-4" onSubmit={saveLot}>
            <SelectInput label="Product" value={lotForm.itemId} onChange={(value) => setLotForm((form) => ({ ...form, itemId: value }))} required options={catalogProducts.map((product) => ({ value: product.id, label: product.name }))} />
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <SuggestionInput label="Brand" value={lotForm.brand} onChange={(value) => setLotForm((form) => ({ ...form, brand: value }))} options={productOptions.brands} placeholder="Search or add a brand" emptyMessage="Start typing to filter saved brands." />
              <TextInput label="Supplier" value={lotForm.supplier} onChange={(value) => setLotForm((form) => ({ ...form, supplier: value }))} />
              <SelectInput label="Supplier Record" value={lotForm.supplierId} onChange={(value) => setLotForm((form) => ({ ...form, supplierId: value }))} options={[{ value: '', label: 'No supplier' }, ...advanced.suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name }))]} />
              <TextInput label="Lot Number" value={lotForm.lotNumber} onChange={(value) => setLotForm((form) => ({ ...form, lotNumber: value }))} required />
              <TextInput label="Expiration Date" type="date" value={lotForm.expirationDate} onChange={(value) => setLotForm((form) => ({ ...form, expirationDate: value }))} />
              <TextInput label="Received Date" type="date" value={lotForm.receivedDate} onChange={(value) => setLotForm((form) => ({ ...form, receivedDate: value }))} />
              <NumberInput label="Unit Cost" value={lotForm.unitCost} onChange={(value) => setLotForm((form) => ({ ...form, unitCost: value }))} step={0.01} />
              <TextInput label="Notes" value={lotForm.notes} onChange={(value) => setLotForm((form) => ({ ...form, notes: value }))} />
            </div>
            <DialogActions saving={saving} saveLabel="Save lot/batch" onCancel={() => setActiveDialog(null)} />
          </form>
        </Dialog>
      ) : null}

      {activeDialog === 'movement' ? (
        <Dialog title={movementForm.type === 'TREATMENT_USAGE' ? 'Use Stock' : 'Adjust Stock'} onClose={() => setActiveDialog(null)}>
          <form className="space-y-4" onSubmit={saveMovement}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <SelectInput label="Product" value={movementForm.itemId} onChange={(value) => {
                const product = products.find((item) => item.id === value);
                setMovementForm((form) => ({ ...form, itemId: value, lotId: '', unit: product?.baseUnit || form.unit, unitId: product?.baseUnitId || '' }));
                if (value) void loadItemUnits(value);
              }} required options={products.map((product) => ({ value: product.id, label: product.name }))} />
              {movementTracksLots ? <SelectInput label="Lot" value={movementForm.lotId} onChange={(value) => setMovementForm((form) => ({ ...form, lotId: value }))} options={[{ value: '', label: 'No lot' }, ...lotOptions.map((lot) => ({ value: lot.lotId || '', label: lot.lotNumber || 'Unnamed lot' }))]} /> : null}
              <SelectInput label="Location" value={movementForm.locationId} onChange={(value) => setMovementForm((form) => ({ ...form, locationId: value }))} required options={locations.map((location) => ({ value: location.id, label: location.name }))} />
              {movementForm.type === 'TREATMENT_USAGE' ? null : (
                <SelectInput label="Reason for change" value={movementForm.type} onChange={(value) => setMovementForm((form) => ({ ...form, type: value }))} required options={[
                  { value: 'PURCHASE_RECEIVED', label: 'Purchase received' },
                  { value: 'MANUAL_ADJUSTMENT', label: 'Manual adjustment' },
                  { value: 'DAMAGED', label: 'Damaged / write-off' },
                  { value: 'EXPIRED', label: 'Expired / write-off' },
                  { value: 'RETURN_TO_SUPPLIER', label: 'Return to supplier' },
                  { value: 'CLIENT_RETURN', label: 'Client return' },
                ]} />
              )}
              <div className="space-y-1">
                <div className="grid grid-cols-[minmax(0,1.25fr)_minmax(120px,0.85fr)] gap-3">
                <NumberInput
                  label={movementForm.type === 'TREATMENT_USAGE' ? 'Quantity used' : 'Stock change'}
                  value={movementForm.type === 'TREATMENT_USAGE' ? Math.abs(movementForm.quantityDelta || 1) : movementForm.quantityDelta}
                  onChange={(value) => setMovementForm((form) => ({ ...form, quantityDelta: form.type === 'TREATMENT_USAGE' ? -Math.abs(value) : value }))}
                  min={movementForm.type === 'TREATMENT_USAGE' ? 0.01 : -100000}
                  step={0.01}
                />
                <SelectInput label="Unit" value={movementForm.unitId || movementForm.unit} onChange={(value) => {
                  const rule = productUnitRules(movementForm.itemId).find((entry) => entry.unitId === value || entry.abbreviation === value);
                  setMovementForm((form) => ({ ...form, unitId: rule?.unitId || '', unit: rule?.abbreviation || value }));
                }} required options={movementUnitOptions.length ? movementUnitOptions : [{ value: movementForm.unit, label: movementForm.unit || 'unit' }]} />
                </div>
                {movementForm.type === 'TREATMENT_USAGE' ? null : <p className="text-xs text-slate-500">Stock change (+ adds, - removes). Use a positive value to add stock or a negative value to remove stock.</p>}
              </div>
              <NumberInput label="Unit Cost" value={movementForm.unitCost} onChange={(value) => setMovementForm((form) => ({ ...form, unitCost: value }))} step={0.01} />
              <TextInput label="Reason" value={movementForm.reason} onChange={(value) => setMovementForm((form) => ({ ...form, reason: value }))} />
              <TextInput label="Reference number (optional)" value={movementForm.reference} onChange={(value) => setMovementForm((form) => ({ ...form, reference: value }))} />
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {movementForm.type === 'TREATMENT_USAGE'
                ? 'This removes stock from the selected location. Visit-linked usage is recorded from the visit completion screen.'
                : 'Example: enter 5 to add five units, or -2 to remove two units.'}
              <span className="mt-1 block font-medium text-slate-700">Base stock change: {baseUnitPreview(movementForm.itemId, movementForm.quantityDelta, movementForm.unitId, movementForm.unit)}</span>
            </div>
            <DialogActions saving={saving} saveLabel={movementForm.type === 'TREATMENT_USAGE' ? 'Save usage' : 'Save adjustment'} onCancel={() => setActiveDialog(null)} />
          </form>
        </Dialog>
      ) : null}

      {activeDialog === 'supplier' ? (
        <Dialog title="Add Supplier" onClose={() => setActiveDialog(null)}>
          <form className="space-y-4" onSubmit={saveSupplier}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <TextInput label="Name" value={supplierForm.name} onChange={(value) => setSupplierForm((form) => ({ ...form, name: value }))} required />
              <TextInput label="Contact Name" value={supplierForm.contactName} onChange={(value) => setSupplierForm((form) => ({ ...form, contactName: value }))} />
              <TextInput label="Email" type="email" value={supplierForm.email} onChange={(value) => setSupplierForm((form) => ({ ...form, email: value }))} />
              <TextInput label="Phone" value={supplierForm.phone} onChange={(value) => setSupplierForm((form) => ({ ...form, phone: value }))} />
              <TextInput label="Address" value={supplierForm.address} onChange={(value) => setSupplierForm((form) => ({ ...form, address: value }))} />
              <TextInput label="Notes" value={supplierForm.notes} onChange={(value) => setSupplierForm((form) => ({ ...form, notes: value }))} />
            </div>
            <DialogActions saving={saving} saveLabel="Add supplier" onCancel={() => setActiveDialog(null)} />
          </form>
        </Dialog>
      ) : null}

      {activeDialog === 'purchaseOrder' ? (
        <Dialog title={purchaseOrderForm.id ? 'Edit Order' : 'Create Order'} onClose={() => setActiveDialog(null)}>
          <form className="space-y-4" onSubmit={savePurchaseOrder}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <SelectInput label="Supplier" value={purchaseOrderForm.supplierId} onChange={(value) => setPurchaseOrderForm((form) => ({ ...form, supplierId: value }))} options={[{ value: '', label: 'No supplier' }, ...advanced.suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name }))]} />
              <TextInput label="Expected Date" type="date" value={purchaseOrderForm.expectedAt} onChange={(value) => setPurchaseOrderForm((form) => ({ ...form, expectedAt: value }))} />
            </div>
            <TextInput label="Notes" value={purchaseOrderForm.notes} onChange={(value) => setPurchaseOrderForm((form) => ({ ...form, notes: value }))} />
            <section className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-ink">Order lines</h3>
                  <p className="text-xs text-slate-500">Add multiple products to a single purchase order.</p>
                </div>
                <button type="button" onClick={addPurchaseOrderLine} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Add item</button>
              </div>
              <div className="space-y-3">
                {purchaseOrderForm.lines.map((line, index) => {
                  const product = catalogProducts.find((entry) => entry.id === line.itemId);
                  return <div key={line.clientId} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-800">Line {index + 1}</div>
                        <div className="text-xs text-slate-500">Quantities are ordered in {product?.baseUnit || 'base stock units'}.</div>
                      </div>
                      <button type="button" onClick={() => removePurchaseOrderLine(line.clientId)} disabled={purchaseOrderForm.lines.length === 1} className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:border-rose-200 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40">Remove</button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1.5fr)_minmax(140px,0.8fr)_minmax(140px,0.8fr)]">
                      <SelectInput label="Product" value={line.itemId} onChange={(value) => {
                        const nextProduct = catalogProducts.find((entry) => entry.id === value);
                        updatePurchaseOrderLine(line.clientId, { itemId: value, unitCost: Number(nextProduct?.costPrice || 0) });
                      }} required options={catalogProducts.map((productOption) => ({ value: productOption.id, label: `${productOption.name}${productOption.packageSize ? ` · ${productOption.packageSize}` : ''}` }))} />
                      <NumberInput label="Ordered Quantity" value={line.orderedQty} onChange={(value) => updatePurchaseOrderLine(line.clientId, { orderedQty: value })} min={0.01} step={0.01} />
                      <NumberInput label="Unit Cost" value={line.unitCost} onChange={(value) => updatePurchaseOrderLine(line.clientId, { unitCost: value })} min={0} step={0.01} />
                    </div>
                  </div>;
                })}
              </div>
            </section>
            <DialogActions saving={saving} saveLabel={purchaseOrderForm.id ? 'Save changes' : 'Create order'} onCancel={() => setActiveDialog(null)} />
          </form>
        </Dialog>
      ) : null}

      {activeDialog === 'purchaseOrderReceive' ? (
        <Dialog title="Receive Purchase Order" onClose={() => setActiveDialog(null)}>
          <form className="space-y-4" onSubmit={savePurchaseOrderReceipt}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <SelectInput label="Location" value={purchaseOrderReceiveForm.locationId} onChange={(value) => setPurchaseOrderReceiveForm((form) => ({ ...form, locationId: value }))} required options={locations.map((location) => ({ value: location.id, label: location.name }))} />
              <TextInput label="Received Date" type="date" value={purchaseOrderReceiveForm.receivedDate} onChange={(value) => setPurchaseOrderReceiveForm((form) => ({ ...form, receivedDate: value }))} />
            </div>
            <TextInput label="Notes" value={purchaseOrderReceiveForm.notes} onChange={(value) => setPurchaseOrderReceiveForm((form) => ({ ...form, notes: value }))} />
            <section className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
              <div>
                <h3 className="text-sm font-semibold text-ink">Lines to receive</h3>
                <p className="text-xs text-slate-500">Leave a line at 0 if it is not being received yet.</p>
              </div>
              <div className="space-y-3">
                {purchaseOrderReceiveForm.lines.map((line) => {
                  const product = catalogProducts.find((entry) => entry.id === line.itemId);
                  const lineUnitOptions = unitOptionsForItem(line.itemId, 'purchase');
                  const lineLotOptions = receiptLotOptionsForItem(line.itemId);
                  const tracksLots = Boolean(product?.trackLots);
                  const tracksExpiry = Boolean(product?.trackExpiry);
                  const isCreatingNewLot = !line.lotId;
                  return <div key={line.lineId} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-800">{line.itemName}</div>
                        <div className="text-xs text-slate-500">Remaining to receive: {formatInventoryMeasurement(line.openQuantity, product?.baseUnit || line.unit || 'unit')}</div>
                      </div>
                      <div className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">PO line</div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(150px,0.9fr)_minmax(130px,0.8fr)]">
                      <label className="block space-y-1 text-sm">
                        <span className="font-medium text-slate-600">Quantity Received</span>
                        <input type="number" min={0} step={0.01} className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10" value={line.quantity} onChange={(event) => updatePurchaseOrderReceiveLine(line.lineId, { quantity: Number(event.target.value) || 0 })} />
                      </label>
                      <SelectInput label="Unit" value={line.unitId || line.unit} onChange={(value) => {
                        const rule = productUnitRules(line.itemId).find((entry) => entry.unitId === value || entry.abbreviation === value);
                        updatePurchaseOrderReceiveLine(line.lineId, { unitId: rule?.unitId || '', unit: rule?.abbreviation || value });
                      }} required options={lineUnitOptions.length ? lineUnitOptions : [{ value: line.unit, label: line.unit || 'unit' }]} />
                      <NumberInput label="Unit Cost" value={line.unitCost} onChange={(value) => updatePurchaseOrderReceiveLine(line.lineId, { unitCost: value })} min={0} step={0.01} />
                    </div>
                    {tracksLots ? (
                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <SelectInput label="Lot/batch" value={line.lotId} onChange={(value) => updatePurchaseOrderReceiveLine(line.lineId, { lotId: value, lotNumber: value ? '' : line.lotNumber, expirationDate: value ? '' : line.expirationDate })} options={[{ value: '', label: 'New lot/batch' }, ...lineLotOptions.map((lot) => ({ value: lot.lotId || '', label: lot.lotNumber || 'Unnamed lot' }))]} />
                        {isCreatingNewLot ? <TextInput label="New lot/batch number" value={line.lotNumber} onChange={(value) => updatePurchaseOrderReceiveLine(line.lineId, { lotNumber: value })} /> : <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">Receiving into the selected existing lot/batch. Lot number and expiry stay unchanged.</div>}
                        {tracksExpiry && isCreatingNewLot ? <TextInput label="Expiration Date" type="date" value={line.expirationDate} onChange={(value) => updatePurchaseOrderReceiveLine(line.lineId, { expirationDate: value })} /> : null}
                      </div>
                    ) : null}
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                      Adds {baseUnitPreview(line.itemId, line.quantity, line.unitId, line.unit)} to stock.
                    </div>
                  </div>;
                })}
              </div>
            </section>
            <DialogActions saving={saving} saveLabel="Receive selected lines" onCancel={() => setActiveDialog(null)} />
          </form>
        </Dialog>
      ) : null}

      {activeDialog === 'goodsReceipt' ? (
        <Dialog title="Receive Stock" onClose={() => setActiveDialog(null)}>
          <form className="space-y-4" onSubmit={saveGoodsReceipt}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <SelectInput label="Product" value={goodsReceiptForm.itemId} onChange={(value) => {
                const product = catalogProducts.find((item) => item.id === value);
                setGoodsReceiptForm((form) => ({ ...form, itemId: value, lotId: '', lotNumber: '', expirationDate: '', unit: product?.baseUnit || form.unit, unitId: product?.baseUnitId || '' }));
                if (value) void loadItemUnits(value);
              }} required options={catalogProducts.map((product) => ({ value: product.id, label: `${product.name}${product.packageSize ? ` · ${product.packageSize}` : ''}` }))} />
              {receiptTracksLots ? <SelectInput label="Lot/batch" value={goodsReceiptForm.lotId} onChange={(value) => setGoodsReceiptForm((form) => ({ ...form, lotId: value, lotNumber: value ? '' : form.lotNumber, expirationDate: value ? '' : form.expirationDate }))} options={[{ value: '', label: 'New lot/batch' }, ...receiptLotOptions.map((lot) => ({ value: lot.lotId || '', label: lot.lotNumber || 'Unnamed lot' }))]} /> : null}
              {receiptTracksLots ? (isCreatingNewReceiptLot ? <TextInput label="New lot/batch number" value={goodsReceiptForm.lotNumber} onChange={(value) => setGoodsReceiptForm((form) => ({ ...form, lotNumber: value }))} /> : <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">Receiving into the selected existing lot/batch. Lot number and expiry stay unchanged.</div>) : null}
              <SelectInput label="Location" value={goodsReceiptForm.locationId} onChange={(value) => setGoodsReceiptForm((form) => ({ ...form, locationId: value }))} required options={locations.map((location) => ({ value: location.id, label: location.name }))} />
              <div className="grid grid-cols-[minmax(0,1.35fr)_minmax(140px,0.85fr)] gap-3">
                <NumberInput label="Quantity Received" value={goodsReceiptForm.quantity} onChange={(value) => setGoodsReceiptForm((form) => ({ ...form, quantity: value }))} min={0.01} step={0.01} />
                <SelectInput label="Unit" value={goodsReceiptForm.unitId || goodsReceiptForm.unit} onChange={(value) => {
                  const rule = productUnitRules(goodsReceiptForm.itemId).find((entry) => entry.unitId === value || entry.abbreviation === value);
                  setGoodsReceiptForm((form) => ({ ...form, unitId: rule?.unitId || '', unit: rule?.abbreviation || value }));
                }} required options={receiptUnitOptions.length ? receiptUnitOptions : [{ value: goodsReceiptForm.unit, label: goodsReceiptForm.unit || 'unit' }]} />
              </div>
              <NumberInput label="Unit Cost" value={goodsReceiptForm.unitCost} onChange={(value) => setGoodsReceiptForm((form) => ({ ...form, unitCost: value }))} min={0} step={0.01} />
              <TextInput label="Received Date" type="date" value={goodsReceiptForm.receivedDate} onChange={(value) => setGoodsReceiptForm((form) => ({ ...form, receivedDate: value }))} />
              {receiptTracksLots && receiptTracksExpiry && isCreatingNewReceiptLot ? <TextInput label="Expiration Date" type="date" value={goodsReceiptForm.expirationDate} onChange={(value) => setGoodsReceiptForm((form) => ({ ...form, expirationDate: value }))} /> : null}
              <TextInput label="Notes" value={goodsReceiptForm.notes} onChange={(value) => setGoodsReceiptForm((form) => ({ ...form, notes: value }))} />
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Adds {baseUnitPreview(goodsReceiptForm.itemId, goodsReceiptForm.quantity, goodsReceiptForm.unitId, goodsReceiptForm.unit)} to stock.
            </div>
            <DialogActions saving={saving} saveLabel="Receive stock" onCancel={() => setActiveDialog(null)} />
          </form>
        </Dialog>
      ) : null}

      {activeDialog === 'stockCount' ? (
        <Dialog title="Start Stock Count" onClose={() => setActiveDialog(null)}>
          <form className="space-y-4" onSubmit={saveStockCount}>
            <SelectInput label="Location" value={stockCountForm.locationId} onChange={(value) => setStockCountForm((form) => ({ ...form, locationId: value }))} required options={locations.map((location) => ({ value: location.id, label: location.name }))} />
            <TextInput label="Notes" value={stockCountForm.notes} onChange={(value) => setStockCountForm((form) => ({ ...form, notes: value }))} />
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              This creates a draft count for the selected location and immediately opens the first counted-product line.
            </div>
            <DialogActions saving={saving} saveLabel="Start count" onCancel={() => setActiveDialog(null)} />
          </form>
        </Dialog>
      ) : null}

      {activeDialog === 'stockCountLine' ? (
        <Dialog title="Add Counted Product" onClose={() => setActiveDialog(null)}>
          <form className="space-y-4" onSubmit={saveStockCountLine}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <SelectInput label="Product" value={stockCountLineForm.itemId} onChange={(value) => {
                const product = catalogProducts.find((item) => item.id === value);
                setStockCountLineForm((form) => ({ ...form, itemId: value, lotId: '', unit: product?.baseUnit || form.unit, unitId: product?.baseUnitId || '' }));
                if (value) void loadItemUnits(value);
              }} required options={catalogProducts.map((product) => ({ value: product.id, label: product.name }))} />
              {stockCountTracksLots ? <SelectInput label="Lot" value={stockCountLineForm.lotId} onChange={(value) => setStockCountLineForm((form) => ({ ...form, lotId: value }))} options={[{ value: '', label: 'No lot' }, ...rows.filter((row) => row.id === stockCountLineForm.itemId && row.lotId).map((lot) => ({ value: lot.lotId || '', label: lot.lotNumber || 'Unnamed lot' }))]} /> : null}
              <NumberInput label="Expected Quantity" value={stockCountLineForm.expectedQuantity} onChange={(value) => setStockCountLineForm((form) => ({ ...form, expectedQuantity: value }))} step={0.01} />
              <div className="grid grid-cols-[minmax(0,1.25fr)_minmax(120px,0.85fr)] gap-3">
                <NumberInput label="Counted Quantity" value={stockCountLineForm.countedQuantity} onChange={(value) => setStockCountLineForm((form) => ({ ...form, countedQuantity: value }))} step={0.01} />
                <SelectInput label="Counted Unit" value={stockCountLineForm.unitId || stockCountLineForm.unit} onChange={(value) => {
                  const rule = productUnitRules(stockCountLineForm.itemId).find((entry) => entry.unitId === value || entry.abbreviation === value);
                  setStockCountLineForm((form) => ({ ...form, unitId: rule?.unitId || '', unit: rule?.abbreviation || value }));
                }} required options={stockCountUnitOptions.length ? stockCountUnitOptions : [{ value: stockCountLineForm.unit, label: stockCountLineForm.unit || 'unit' }]} />
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Counted total equals {baseUnitPreview(stockCountLineForm.itemId, stockCountLineForm.countedQuantity, stockCountLineForm.unitId, stockCountLineForm.unit)} in base stock.
            </div>
            <DialogActions saving={saving} saveLabel="Save count line" onCancel={() => setActiveDialog(null)} />
          </form>
        </Dialog>
      ) : null}
    </div>
  );
}

function Dialog({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/20 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">{title}</h2>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-ink" aria-label="Close dialog">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AlertCard({ label, value, tone }: { label: string; value: number; tone: 'amber' | 'sky' | 'rose' | 'emerald' | 'slate' }) {
  const toneClass = {
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  }[tone];
  return (
    <div className={`rounded-lg border px-4 py-3 ${toneClass}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
        <AlertTriangle size={14} />
        {label}
      </div>
      <p className="mt-2 text-2xl font-semibold">{value}</p>
    </div>
  );
}

function InventoryFeatureCard({ icon, title, detail }: { icon: React.ReactNode; title: string; detail: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start justify-between gap-3 text-sm font-semibold text-slate-900">
        <span className="flex min-w-0 items-start gap-2.5">
          <span className="mt-0.5 shrink-0 text-sky-600">{icon}</span>
          {title}
        </span>
        <ComingSoonBadge compact />
      </div>
      <p className="mt-2 text-xs leading-5 text-slate-500">{detail}</p>
    </div>
  );
}

function SimpleTable({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <History size={16} className="text-slate-500" />
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
        </div>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">{children}</table>
      </div>
    </section>
  );
}

function DialogActions({ saving, saveLabel = 'Save', onCancel }: { saving: boolean; saveLabel?: string; onCancel: () => void }) {
  return (
    <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
      <button type="button" onClick={onCancel} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
      <button type="submit" disabled={saving} className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Saving...' : saveLabel}</button>
    </div>
  );
}

function CheckboxInput({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
      <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      {label}
    </label>
  );
}

function TextInput({ label, value, onChange, required, type = 'text', placeholder }: { label: string; value: string; onChange: (value: string) => void; required?: boolean; type?: string; placeholder?: string }) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="font-medium text-slate-600">{label}</span>
      <input type={type} placeholder={placeholder} required={required} className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function SuggestionInput({ label, value, onChange, options, placeholder, emptyMessage }: { label: string; value: string; onChange: (value: string) => void; options: string[]; placeholder?: string; emptyMessage?: string }) {
  const [open, setOpen] = useState(false);
  const trimmedValue = value.trim();
  const normalizedValue = trimmedValue.toLowerCase();
  const filteredOptions = useMemo(() => {
    const seen = new Set<string>();
    return options
      .map((option) => option.trim())
      .filter((option) => {
        if (!option) return false;
        const normalizedOption = option.toLowerCase();
        if (seen.has(normalizedOption)) return false;
        seen.add(normalizedOption);
        return !normalizedValue || normalizedOption.includes(normalizedValue);
      })
      .slice(0, 8);
  }, [normalizedValue, options]);
  const exactMatch = options.some((option) => option.trim().toLowerCase() === normalizedValue);
  const showCreateOption = Boolean(trimmedValue) && !exactMatch;

  return (
    <label className="block space-y-1 text-sm">
      <span className="font-medium text-slate-600">{label}</span>
      <div className="relative">
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
        />
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setOpen((current) => !current)}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 transition hover:text-slate-600"
          aria-label={`Toggle ${label.toLowerCase()} suggestions`}
        >
          <ChevronDown size={16} className={open ? 'rotate-180' : ''} />
        </button>
        {open ? (
          <div className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl ring-1 ring-slate-100">
            {filteredOptions.map((option) => (
              <button
                key={option}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                className="block w-full border-b border-slate-100 px-3 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50 last:border-b-0"
              >
                {option}
              </button>
            ))}
            {showCreateOption ? (
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(trimmedValue);
                  setOpen(false);
                }}
                className="block w-full border-t border-slate-100 bg-slate-50 px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              >
                Add &quot;{trimmedValue}&quot;
              </button>
            ) : null}
            {!filteredOptions.length && !showCreateOption ? (
              <div className="px-3 py-2.5 text-sm text-slate-500">{emptyMessage || 'No saved options yet.'}</div>
            ) : null}
          </div>
        ) : null}
      </div>
    </label>
  );
}

function NumberInput({ label, value, onChange, min = 0, step = 1 }: { label: string; value: number; onChange: (value: number) => void; min?: number; step?: number }) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="font-medium text-slate-600">{label}</span>
      <input type="number" min={min} step={step} className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10" value={value} onChange={(event) => onChange(Number(event.target.value) || 0)} />
    </label>
  );
}

function SelectInput({ label, value, onChange, options, required }: { label: string; value: string; onChange: (value: string) => void; options: Array<{ value: string; label: string }>; required?: boolean }) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="font-medium text-slate-600">{label}</span>
      <select required={required} className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10" value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => <option key={`${option.value}-${option.label}`} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}
